import {
  AttributeHeader,
  AttributeValue,
  Button,
  Column,
  DATE_FORMATTER_STR_WITH_TIME,
  Filters,
  formatDate,
  Header,
  Typography,
  isAfter,
  Row,
  ScrollContainer,
  Search,
  Select,
  StickyFooterButtons,
  SubSection,
  TeamLogo,
  toMoney,
  useLoading,
  ValueOpt,
} from "best-common-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { exportPurchaseOpportunity, runPurchaseSelection, sendPurchaseEmails } from "../../../api/RequesTixApi";
import AdminPurchaseOpportunityGame from "../../../components/purchaseOpportunity/AdminPurchaseOpportunityGame";
import RouteConstants from "../../../constants/RouteConstants";
import { queries } from "../../../queries";
import {
  AdminPurchaseOpportunityOverviewDTO,
  AdminPurchaseOpportunityOverviewGameDTO,
  PurchaseOpportunityFilters,
  PurchaseOpportunityStatus,
  PurchaseOpportunityStatusId,
} from "../../../types/PurchaseOpportunity";
import { downloadLink, FileTypes } from "../../../util/FileUtil";

type RouteParams = {
  id: string;
};

const AdminPurchaseOpportunityView = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const { id } = useParams<RouteParams>();
  const purchaseOpportunityId = Number(id);
  const [filters, setFilters] = useState<PurchaseOpportunityFilters>({});
  const [bulkProcessing, setBulkProcessing] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const overviewQuery = queries.purchaseOpportunity.getAdminPurchaseOpportunityOverview(purchaseOpportunityId);

  const { data: opp, isLoading } = useQuery<AdminPurchaseOpportunityOverviewDTO>({
    ...overviewQuery,
    enabled: Number.isInteger(purchaseOpportunityId) && purchaseOpportunityId > 0,
    refetchOnWindowFocus: !bulkProcessing,
    refetchOnReconnect: !bulkProcessing,
  });

  const reload = async () => {
    await queryClient.invalidateQueries(
      {
        queryKey: overviewQuery.queryKey,
        exact: true,
        refetchType: "active",
      },
      { throwOnError: true },
    );
  };

  const runSelectionMutation = useMutation({
    mutationFn: () => runPurchaseSelection(opp!.purchaseOpportunityId!),
    onSuccess: reload,
  });

  const sendEmailsMutation = useMutation({
    mutationFn: () => sendPurchaseEmails(opp!.purchaseOpportunityId!),
    onSuccess: reload,
  });

  const allGamesProcessed = useMemo(
    () => !!opp && !opp.purchaseOpportunityGames.some((game) => !game.processed),
    [opp],
  );
  const pastCutoff = useMemo(
    () => !!opp?.cutoffDate && isAfter(new Date(), new Date(opp.cutoffDate)),
    [opp?.cutoffDate],
  );
  const orderedGames = useMemo(
    () => [...(opp?.purchaseOpportunityGames ?? [])].sort((first, second) => first.gameNumber - second.gameNumber),
    [opp?.purchaseOpportunityGames],
  );
  const purchaseOpportunityStatusOptions = useMemo(() => {
    const statuses = new Map<PurchaseOpportunityStatusId, PurchaseOpportunityStatus>();
    for (const game of opp?.purchaseOpportunityGames ?? []) {
      for (const entry of game.adminEntries) {
        statuses.set(entry.purchaseOpportunityStatus.purchaseOpportunityStatusId, entry.purchaseOpportunityStatus);
      }
    }
    return Array.from(statuses.values()).map((status) => ({ label: status.description, value: status }));
  }, [opp?.purchaseOpportunityGames]);
  const pageBusy = bulkProcessing || isLoading || runSelectionMutation.isPending || sendEmailsMutation.isPending;

  const onFiltersChange = (key: keyof PurchaseOpportunityFilters, value: string | PurchaseOpportunityStatusId[]) => {
    setFilters((currentFilters) => ({ ...currentFilters, [key]: value }));
  };

  const exportData = async () => {
    if (!opp?.purchaseOpportunityId) {
      return;
    }
    try {
      setLoading(true);
      const response = await exportPurchaseOpportunity(opp.purchaseOpportunityId);
      downloadLink(response, FileTypes.EXCEL);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  return (
    <div>
      <Header
        sticky
        flexColumn
        title={
          <div className="d-flex justify-content-between align-items-center bcr-gap-2 w-100">
            <div className="d-flex align-items-center bcr-gap-2">
              {!!opp?.teamId && <TeamLogo teamId={opp.teamId} size="2.5rem" />}
              <Typography elementType="h1" variant="headlineSmall">
                Purchase Opportunity
              </Typography>
            </div>
            <StickyFooterButtons startsOpen={true}>
              <Button
                variant="primary"
                disabled={pageBusy || allGamesProcessed || !pastCutoff}
                onClick={() => runSelectionMutation.mutate()}
              >
                Run Selection
              </Button>
              <Button
                variant="primary"
                disabled={pageBusy || !allGamesProcessed || opp?.emailsSent}
                onClick={() => sendEmailsMutation.mutate()}
              >
                Send Emails
              </Button>
              <Button
                variant="secondary"
                onClick={() => void exportData()}
                disabled={pageBusy || !opp?.purchaseOpportunityId}
              >
                Export
              </Button>
              <Button
                variant="default"
                disabled={bulkProcessing}
                onClick={() => navigate(RouteConstants.ADMIN.PURCHASE_OPPORTUNITY.BASE)}
              >
                Go Back
              </Button>
            </StickyFooterButtons>
          </div>
        }
      >
        <Filters>
          <Search
            id="freeText"
            label="Search"
            placeholder="Search by user name or email address"
            value={filters.freeText}
            onChange={(value: string) => onFiltersChange("freeText", value)}
            gutterBottom
            disabled={bulkProcessing}
          />
          <Select
            id="statusIds"
            label="Status"
            isMulti
            value={purchaseOpportunityStatusOptions.filter((option: ValueOpt<PurchaseOpportunityStatus>) =>
              filters.statusIds?.includes(option.value.purchaseOpportunityStatusId),
            )}
            onChange={(values: ValueOpt<PurchaseOpportunityStatus>[]) =>
              onFiltersChange(
                "statusIds",
                values.map((value: ValueOpt<PurchaseOpportunityStatus>) => value.value.purchaseOpportunityStatusId),
              )
            }
            options={purchaseOpportunityStatusOptions}
            gutterBottom
            disabled={bulkProcessing}
          />
        </Filters>
      </Header>

      <ScrollContainer className="bcr-p-2 bcr-pb-5">
        {!!opp && (
          <>
            <SubSection header="Opportunity Details">
              <Row>
                <Column width={4}>
                  <AttributeHeader>Year</AttributeHeader>
                  <AttributeValue>{opp.year}</AttributeValue>
                </Column>
                <Column width={4}>
                  <AttributeHeader>Type</AttributeHeader>
                  <AttributeValue>{opp.seriesType?.description}</AttributeValue>
                </Column>
                <Column width={4}>
                  <AttributeHeader>Number of Winners</AttributeHeader>
                  <AttributeValue>{opp.numOfWinners}</AttributeValue>
                </Column>
                <Column width={4}>
                  <AttributeHeader>Games</AttributeHeader>
                  <AttributeValue>
                    {orderedGames.map((game) => `Home Game #${game.gameNumber}`).join(", ")}
                  </AttributeValue>
                </Column>
                <Column width={4}>
                  <AttributeHeader>Access Date</AttributeHeader>
                  <AttributeValue>
                    {!!opp.accessDate && formatDate(new Date(opp.accessDate), DATE_FORMATTER_STR_WITH_TIME)}
                  </AttributeValue>
                </Column>
                <Column width={4}>
                  <AttributeHeader>Cutoff Date</AttributeHeader>
                  <AttributeValue>
                    {!!opp.cutoffDate && formatDate(new Date(opp.cutoffDate), DATE_FORMATTER_STR_WITH_TIME)}
                  </AttributeValue>
                </Column>
                <Column width={4}>
                  <AttributeHeader>Ticket Cost</AttributeHeader>
                  <AttributeValue>{toMoney(opp.ticketCost)}</AttributeValue>
                </Column>
                <Column width={1}>
                  <AttributeHeader>Cost Description</AttributeHeader>
                  <AttributeValue>{opp.costDescription}</AttributeValue>
                </Column>
              </Row>
            </SubSection>

            <div className="d-flex flex-column bcr-gap-2 bcr-mt-2">
              {orderedGames.map((game: AdminPurchaseOpportunityOverviewGameDTO) => (
                <AdminPurchaseOpportunityGame
                  opp={opp}
                  key={game.purchaseOpportunityGameId}
                  game={game}
                  filters={filters}
                  reload={reload}
                  actionsDisabled={pageBusy}
                  onProcessingChange={setBulkProcessing}
                />
              ))}
            </div>
          </>
        )}
      </ScrollContainer>
    </div>
  );
};

export default AdminPurchaseOpportunityView;
